import React, { useState, useMemo } from 'react'
import {
  ListItem,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  FormControl,
} from '@material-ui/core'
import Checkbox from 'components/common/Checkbox'
import Radio from 'components/common/Radio'
import isEqual from 'lodash/isEqual'
import { injectIntl } from 'react-intl'
import cn from 'classnames'
import { ReactComponent as Vector } from 'assets/img/select-vector.svg'
import { PURPLE, PRIMARY_TEXT } from 'constants/colors'
import {
  StyledClearSelectMenuItem,
  StyledClearSelectButton,
} from 'routes/App/Apps/MyJobs/JobFields/JobFields.styles'
import Loader from 'components/Loading/SmallLoader'
import messages from '../translate'
import {
  StyledMenuItem,
  StyledSelectInput,
  EmptyState,
  LoaderContainer,
} from '../styles'
import { compareValues } from '../utils'
import SelectInputBase from '../SelectInputBase'
import { ListItemWrapper } from './NestedSelectInput.styles'

function NestedSelectInput({
  intl: { formatMessage },
  multiple = false,
  hasSelectAllOption = false,
  hasSearchField = false,
  titlesTranslatable = false,
  fullWidth = false,
  label = formatMessage(messages.select),
  options = [],
  required = false,
  isValidationFailed,
  onChange,
  onClose,
  selectInputRef,
  searchInputRef,
  selectedValue,
  setSelectedValue,
  searchQuery,
  setSearchQuery,
  filteredOptions,
  setFilteredOptions,
  isEmptySelect,
  isSelectInputDisabled,
  onCloseSelectInput,
  emptyStateText = formatMessage(messages.noRelevantInputs),
  startAdornment: StartAdornment = null,
  hasClearOption = false,
  groupSelectable = false,
  onOpen,
  isLoading,
  hideEmptyGroups = true,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [displayValue, setDisplayValue] = useState('')

  const defaultValue = useMemo(() => (multiple ? [] : null), [multiple])

  const getAllOptions = () => {
    let allOptions = []

    Object.keys(filteredOptions).forEach(key => {
      allOptions = [
        ...allOptions,
        ...filteredOptions[key].options.map(({ value }) => value),
      ]
    })

    return allOptions
  }

  const renderSelectAllOption = () => {
    const allOptions = getAllOptions()
    const allSelected = selectedValue.length === allOptions.length
    const noneSelected = selectedValue.length === 0
    const someSelected = !allSelected && !noneSelected

    return (
      <StyledMenuItem
        onClick={event => {
          event.stopPropagation()
        }}
        className={cn('menu-item', { active: allSelected || someSelected })}>
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={event => {
            event.preventDefault()
          }}
          color={PURPLE}
        />
        {formatMessage(messages.selectAll)}
      </StyledMenuItem>
    )
  }

  const renderValues = () => {
    if (isEmptySelect && !displayValue.length) {
      return `${label}${required ? ' *' : ''}`
    }

    let optionsList = []

    options.forEach(({ options, id, title }) => {
      optionsList = [
        ...optionsList,
        ...options,
        ...(id ? [{ value: id, label: title }] : []),
      ]
    })

    const displayLabel = multiple
      ? optionsList
          .filter(({ value }) =>
            selectedValue.some(selected => compareValues(selected, value)),
          )
          .map(({ label }) => label)
          .join(', ')
      : optionsList.find(({ value }) => compareValues(value, selectedValue)) &&
        optionsList.find(({ value }) => compareValues(value, selectedValue))
          .label

    if (selectedValue?.length && displayLabel?.length) {
      setDisplayValue(displayLabel)
    }

    return selectedValue?.length && !displayLabel?.length
      ? displayValue
      : displayLabel || `${label}${required ? ' *' : ''}`
  }

  const onChangeSearchField = ({ target: { value: targetValue } }) => {
    const filtered = []
    options.forEach(item => {
      const filteredOptions = item.options.filter(
        ({ label }) =>
          label.toLowerCase().indexOf(targetValue.toLowerCase()) >= 0,
      )
      const filteredItem = {
        ...item,
        options: filteredOptions,
      }

      filtered.push(filteredItem)
    })

    setFilteredOptions(filtered)
    setSearchQuery(targetValue)
  }

  const onChangeSelectInput = event => {
    const menuItem = event.nativeEvent.target.closest('.menu-item')

    if (!menuItem) {
      return
    }

    const {
      attributes: { value: { value: optionValue = 'SELECT_ALL' } = {} },
    } = menuItem
    let newValue = optionValue

    if (multiple) {
      if (optionValue === 'SELECT_ALL') {
        const allOptions = getAllOptions()

        const allSelected = selectedValue.length === allOptions.length

        newValue = allSelected ? [] : allOptions
        setSelectedValue(newValue)
      } else if (
        options.some(option => compareValues(option.id, optionValue))
          ?.options &&
        groupSelectable
      ) {
        const nestedOptions = options
          .find(option => compareValues(option.id, optionValue))
          .options.map(option => option.value.toString())

        if (
          nestedOptions.every(option =>
            selectedValue.some(val => compareValues(val, option)),
          )
        ) {
          newValue = selectedValue.filter(
            val => !nestedOptions.some(option => compareValues(val, option)),
          )
        } else {
          newValue = [...selectedValue, ...nestedOptions]
        }

        if (!nestedOptions.length && groupSelectable) {
          if (selectedValue.some(val => compareValues(val, optionValue))) {
            newValue = selectedValue.filter(
              val => !compareValues(val, optionValue),
            )
          } else {
            newValue = [...selectedValue, optionValue]
          }
        }
        setSelectedValue(newValue)
      } else {
        const isSelected = selectedValue.some(val =>
          compareValues(val, optionValue),
        )

        if (isSelected) {
          newValue = selectedValue.filter(
            val => !compareValues(val, optionValue),
          )

          setSelectedValue(newValue)
        } else {
          newValue = [...selectedValue, optionValue]

          setSelectedValue(newValue)
        }
      }
    } else {
      setSelectedValue(newValue)
    }

    if (onClose && !multiple) {
      onClose(optionValue)
    }

    if (onChange) {
      onChange(newValue)
    }
  }

  return (
    <StyledSelectInput
      error={isValidationFailed}
      $hasAdornment={!!StartAdornment}>
      <FormControl disabled={isSelectInputDisabled} fullWidth={fullWidth}>
        <Select
          IconComponent={Vector}
          displayEmpty
          multiple={multiple}
          required={required}
          className="job-field-input-box"
          disabled={isSelectInputDisabled}
          value={selectedValue}
          ref={selectInputRef}
          MenuProps={{
            className: cn(
              'triggers-select-dropdown',
              { multiple },
              { 'has-search': hasSearchField },
            ),
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            getContentAnchorEl: null,
          }}
          onChange={event => {
            onChangeSelectInput(event)
          }}
          renderValue={renderValues}
          onOpen={() => {
            setIsOpen(true)
            setTimeout(() => {
              if (searchInputRef.current) {
                searchInputRef.current.focus()
              }
            }, 0)
            if (onOpen) {
              onOpen()
            }
          }}
          onClose={props => {
            setIsOpen(false)
            onCloseSelectInput(props)
          }}
          inputProps={{
            className: 'job-field-input',
          }}
          startAdornment={
            StartAdornment && (
              <InputAdornment position="start">
                <StartAdornment color={isOpen ? PURPLE : PRIMARY_TEXT} />
              </InputAdornment>
            )
          }>
          {hasSearchField && (
            <MenuItem
              className="jobs-triggers-input"
              onKeyDown={event => {
                event.stopPropagation()
              }}
              button={false}>
              <TextField
                fullWidth
                placeholder={formatMessage(messages.search)}
                value={searchQuery}
                inputProps={{
                  ref: searchInputRef,
                }}
                onClick={event => {
                  event.stopPropagation()
                }}
                onChange={onChangeSearchField}
              />
            </MenuItem>
          )}
          {isLoading && (
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          )}
          {!isLoading && !isSelectInputDisabled && !filteredOptions.length && (
            <EmptyState>{emptyStateText}</EmptyState>
          )}
          {hasSelectAllOption &&
            multiple &&
            !!filteredOptions.length &&
            renderSelectAllOption()}
          {hasClearOption && !isEqual(selectedValue, defaultValue) && (
            <StyledClearSelectMenuItem
              onKeyDown={e => {
                e.stopPropagation()
              }}>
              <StyledClearSelectButton
                onClick={e => {
                  e.stopPropagation()

                  onChange(defaultValue)
                }}>
                {formatMessage(messages.clearSelection)}
              </StyledClearSelectButton>
            </StyledClearSelectMenuItem>
          )}
          {filteredOptions.map(({ id, title, options: nestedOptions }) => {
            if (!nestedOptions.length && hideEmptyGroups) {
              return null
            }

            const allSelected =
              (nestedOptions.every(option =>
                selectedValue.some(val => compareValues(val, option.value)),
              ) &&
                nestedOptions.length) ||
              selectedValue.some(val => compareValues(val, id))
            const noneSelected =
              !selectedValue.some(val =>
                nestedOptions.find(option => compareValues(val, option.value)),
              ) || !nestedOptions.length

            const someSelected = !allSelected && !noneSelected
            const displayTitle = titlesTranslatable
              ? formatMessage(messages[title])
              : title

            return (
              <div
                key={title}
                onClick={event => {
                  event.stopPropagation()
                }}>
                {!groupSelectable ? (
                  <ListItemWrapper>
                    <ListItem
                      onClick={event => {
                        event.stopPropagation()
                      }}>
                      {displayTitle}
                    </ListItem>
                  </ListItemWrapper>
                ) : (
                  <StyledMenuItem
                    value={id}
                    className={cn('menu-item', { active: allSelected })}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={event => {
                        event.preventDefault()
                      }}
                      color={PURPLE}
                    />
                    {displayTitle}
                  </StyledMenuItem>
                )}
                {nestedOptions.map(({ value, label: optionLabel }) => {
                  const isSelected = multiple
                    ? selectedValue.some(val => compareValues(val, value))
                    : compareValues(value, selectedValue)

                  return (
                    <StyledMenuItem
                      $nested={groupSelectable}
                      key={value}
                      value={value}
                      className={cn('menu-item', { active: isSelected })}>
                      {multiple ? (
                        <Checkbox
                          checked={isSelected}
                          onClick={event => {
                            event.preventDefault()
                          }}
                          color={PURPLE}
                        />
                      ) : (
                        <Radio
                          checked={isSelected}
                          onClick={event => {
                            event.preventDefault()
                          }}
                          color={PURPLE}
                        />
                      )}
                      {optionLabel}
                    </StyledMenuItem>
                  )
                })}
              </div>
            )
          })}
        </Select>
      </FormControl>
    </StyledSelectInput>
  )
}

export default injectIntl(SelectInputBase(NestedSelectInput))
